import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { Button, DataTable } from '../../../../components/ui';
import { getTenants, tenantKeys } from '../../../../api/tenant.api';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import type { Tenant } from '../../../../types/iam-tenant.types';

const columns: ColumnsType<Tenant> = [
	{
		title: 'Tenant Code',
		dataIndex: 'tenantCode',
		key: 'tenantCode',
		width: 180,
		sorter: (a, b) => a.tenantCode.localeCompare(b.tenantCode),
	},
	{
		title: 'Tenant Name',
		dataIndex: 'tenantName',
		key: 'tenantName',
		width: 180,
		sorter: (a, b) => a.tenantName.localeCompare(b.tenantName),
	},
	{
		title: 'Sub Domain',
		dataIndex: 'subDomain',
		key: 'subDomain',
		width: 180,
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		width: 120,
		align: 'center',
		render: (_: unknown, record: Tenant) => (
			<Tag color={record.status === 'ACTIVE' ? 'green' : 'default'}>
				{record.status === 'ACTIVE' ? 'Active' : 'Inactive'}
			</Tag>
		),
	},
];

interface TenantListViewProps {
	createPermission?: string;
}

export default function TenantListView({ createPermission = 'iam:tenant:create' }: TenantListViewProps) {
	const navigate = useNavigate();

	const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteScroll<Tenant>({
			queryKey: tenantKeys.lists(),
			queryFn: getTenants,
		});

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<p className="text-sm text-gray-500 m-0">
					Manage tenant organizations and their configurations.
				</p>
				<Button
					variant="primary"
					icon={<PlusOutlined />}
					permission={createPermission}
					onClick={() => navigate('/iam/tenants/create')}
				>
					Create Tenant
				</Button>
			</div>

			<DataTable<Tenant>
				columns={columns}
				dataSource={items}
				rowKey="id"
				loading={isLoading}
				onRowClick={(record) => navigate(`/iam/tenants/${record.id}`)}
				infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
			/>
		</div>
	);
}
